import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { ICampaignsTypes } from '../../../model/campaign/ICampaignType';
import { Pagination } from '../../../shared/components/Pagination';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';
import ToggleChip from '../../../shared/components/ToggleChip';
import { CampaignsService } from '../CampaignsService';
import { ErrorToast } from '../../../utils/toasters';

const TYPE_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Title' },
    { label: 'Description' },
    { label: 'Followers range from' },
    { label: 'Followers range to' },
    { label: 'Active' },
    { label: 'Image' },
    { label: 'Edit' },
];

export function CampaignType() {
    const navigate = useNavigate();
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        title: '',
    });

    const [campaignTypes, setCampaignTypes] = useState<ICampaignsTypes[]>([]);
    const [change, setChange] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        setDataLoading(true);
        CampaignsService.getCampaignsTypes(state)
            .then(data => {
                setCampaignTypes(data.data);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [change, page]);

    function handleClick(event: any) {
        event.preventDefault();
        setChange(!change);
    }

    function handleChange(event: any) {
        const value = event.target.value.trim();
        setState({
            ...state,
            [event.target.name]: value,
        });
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setPage(updatePage);
    };

    function handleCheckbox(id: number) {
        CampaignsService.campaignTypeActive(id)
            .then(() => {
                setCampaignTypes(prev =>
                    prev.map(type => (type.id === id ? { ...type, isActive: !type.isActive } : type))
                );
            })
            .catch(err => ErrorToast(err));
    }

    return (
        <>
            <Button variant="outline-primary" onClick={() => navigate('/campaign/types/form')}>
                + Add campaign type
            </Button>

            <Card
                className="mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick(e);
                }}
            >
                <Row>
                    <Form.Group as={Col} className="col-4 mb-0">
                        <Form.Control
                            placeholder="Title"
                            className="autocomplete-input"
                            name="title"
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group as={Col} className="mb-0">
                        <Button type="submit" onClick={handleClick}>
                            Search
                        </Button>
                    </Form.Group>
                </Row>
            </Card>

            <Card className="mb-5 p-4">
                {dataLoading ? (
                    <TableSkeleton columns={TYPE_COLUMNS} rows={state.perPage} />
                ) : (
                    <Table hover responsive>
                        <thead>
                            <tr>
                                {TYPE_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {campaignTypes?.map(type => (
                                <tr key={type.id}>
                                    <td>{type.id}</td>
                                    <td>{type.title}</td>
                                    <td>
                                        {type.description.length > 40
                                            ? type.description.substring(0, 39) + '...'
                                            : type.description ?? '-'}
                                    </td>
                                    <td>{type.followersRangeFrom}</td>
                                    <td>{type.followersRangeTo}</td>
                                    <td>
                                        <ToggleChip
                                            name="isActive"
                                            onChange={() => handleCheckbox(type.id as number)}
                                            checked={!!type.isActive}
                                            label={type.isActive ? 'Active' : 'Inactive'}
                                        />
                                    </td>
                                    <td>
                                        <img
                                            alt="creator type"
                                            src={type?.creatorTypeImage?.thumbnail || '/images/placeholder.png'}
                                            style={{ width: 70, height: 60 }}
                                            className={`rounded ${type?.creatorTypeImage ? 'img-cover' : ''}`}
                                        />
                                    </td>
                                    <td>
                                        <Button onClick={() => navigate(`/campaign/types/form/` + type.id)}>
                                            Edit
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                <Pagination page={page} totalPages={totalPages} handlePagination={handlePages} disabled={dataLoading} />
            </Card>
        </>
    );
}
